import { DistributionCodeData } from '@/components/UIComponents/CommonModal/distribution/hooks/useDistributionCode';
import { getDistributionCode } from '@/components/UIComponents/CommonModal/service';
import { CouponList, getUserCoupon } from '@/components/UIComponents/UserAccount/service';
import UserStore, { CouponInfo } from '@/store/User';

import { COUPON_PRIORITY, MOCK_ENABLE } from '../config';
import { CouponType, StandardCoupon } from '../types';

// Mock 优惠券：新用户专享 30% 折扣，剩余 86400 秒（24小时）
const MOCK_COUPONS: StandardCoupon[] = [
  {
    type: CouponType.NEW_USER_EXCLUSIVE,
    priority: COUPON_PRIORITY.NEW_USER_EXCLUSIVE,
    discount: 30,
    commodityCodes: ['ALL'],
    leftSeconds: 86400,
    rawData: null,
  },
];

export class CouponManager {
  private static instance: CouponManager;
  private coupons: StandardCoupon[] = [];
  private isInitialized = false;

  private constructor() {}

  static getInstance(): CouponManager {
    if (!CouponManager.instance) {
      CouponManager.instance = new CouponManager();
    }
    return CouponManager.instance;
  }

  async fetchAllCoupons(): Promise<StandardCoupon[]> {
    if (MOCK_ENABLE) {
      this.coupons = MOCK_COUPONS;
      this.isInitialized = true;
      return this.coupons;
    }

    try {
      const results = await Promise.allSettled([
        this.fetchDistributionCoupons(),
        this.fetchTicketCoupons(),
      ]);

      this.coupons = results
        .filter(
          (r): r is PromiseFulfilledResult<StandardCoupon[]> => r.status === 'fulfilled',
        )
        .flatMap((r) => r.value)
        .sort((a, b) => b.priority - a.priority);

      this.isInitialized = true;
      return this.coupons;
    } catch (error) {
      console.error('获取优惠券失败:', error);
      return [];
    }
  }

  getTopPriorityCoupon(): StandardCoupon | null {
    if (!this.isInitialized) return null;
    return this.coupons.filter((c) => this.isValid(c))[0] || null;
  }

  getCouponByType(type: CouponType): StandardCoupon | null {
    if (!this.isInitialized) return null;
    return this.coupons.find((c) => c.type === type && this.isValid(c)) || null;
  }

  reset(): void {
    this.coupons = [];
    this.isInitialized = false;
  }

  private async fetchDistributionCoupons(): Promise<StandardCoupon[]> {
    try {
      const { data, success } = await getDistributionCode<DistributionCodeData>(2);
      if (!success || !data) return [];

      const dist = data as DistributionCodeData;
      // codeState Disable 或 pattern=1 时不返回分销券
      if ((dist as any).codeState === 1 || Number(dist.pattern) === 1) return [];

      const coupons: StandardCoupon[] = [];
      if ((dist as any).discountMode !== 3) {
        coupons.push({
          type: CouponType.DISTRIBUTION_ALL,
          priority: COUPON_PRIORITY.DISTRIBUTION_ALL,
          commodityCodes: ['ALL'],
          pattern: Number(dist.pattern),
          rawData: dist,
        });
      } else if ((dist as any).commodityDiscount?.length) {
        const commodityDiscount =
          typeof (dist as any).commodityDiscount === 'string'
            ? JSON.parse((dist as any).commodityDiscount)
            : (dist as any).commodityDiscount;
        coupons.push({
          type: CouponType.DISTRIBUTION_SPECIFIC,
          priority: COUPON_PRIORITY.DISTRIBUTION_SPECIFIC,
          discount: 0,
          commodityCodes: commodityDiscount.map((c: any) => c.commodityCode),
          pattern: Number(dist.pattern),
          rawData: { ...dist, commodityDiscount },
        });
      }
      return coupons;
    } catch {
      return [];
    }
  }

  private async fetchCouponTicketInfo() {
    const { code, data } = await getUserCoupon<CouponList>();
    if (code !== 200 || !data) {
      return {
        type: 'ticket',
        data: [],
        availabled: undefined,
        used: undefined,
        hasCoupon: false,
        isRequestEnd: true,
      };
    }
    const couponList = data as CouponList;

    const hasNewUserExclusive = couponList.filter((item) => item.mode === 1);
    const hasBasicVipExclusive = couponList.filter((item) => item.mode === 2);
    if (hasBasicVipExclusive.length) {
      // 基础版升级的差价优惠
      if (hasBasicVipExclusive.length) {
        const availabled = hasBasicVipExclusive.map((item) => ({
          ...item,
          applicableGoods: (item.applicableGoods as unknown as string).split(',') || [],
        }));
        return {
          type: 'ticket',
          data: couponList,
          availabled,
          used: undefined,
          hasCoupon: true,
          isRequestEnd: true,
          extends: {
            type: 'BASIC_VIP_UPGRADE',
          },
        };
      }
    }
    if (hasNewUserExclusive.length) {
      // 新用户专享的优惠券，其次走可用的优惠券
      const availabled = hasNewUserExclusive.map((item) => ({
        ...item,
        applicableGoods: (item.applicableGoods as unknown as string).split(',') || [],
      }));

      return {
        type: 'ticket',
        data: couponList,
        availabled,
        used: undefined,
        hasCoupon: true,
        isRequestEnd: true,
        extends: {
          type: 'NEW_USER_EXCLUSIVE',
        },
      };
    }
    // 可用的优惠券
    const availabled = couponList
      .filter((item) => item.status === 1 && item.isValidityPeriod)
      .map((item) => ({
        ...item,
        applicableGoods: (item.applicableGoods as unknown as string).split(',') || [],
      }));
    // // 已用的优惠券
    const used = couponList
      .filter((item) => item.status === 2)
      .map((item) => ({
        ...item,
        applicableGoods: (item.applicableGoods as unknown as string).split(',') || [],
      }));

    return {
      type: 'ticket',
      data: couponList,
      availabled: availabled.length ? availabled : undefined,
      used: used.length ? used : undefined,
      hasCoupon: availabled.length ? true : false,
      isRequestEnd: true,
    };
  }

  private async fetchTicketCoupons(): Promise<StandardCoupon[]> {
    try {
      const couponData = await this.fetchCouponTicketInfo();
      UserStore.getState().setCouponInfo(couponData as unknown as CouponInfo);
      const data = couponData?.data;
      if (!data) return [];

      const couponList = data as CouponList;
      const coupons: StandardCoupon[] = [];

      const newUserExclusive = couponList.filter((item) => item.mode === 1);
      if (newUserExclusive.length) {
        coupons.push({
          type: CouponType.NEW_USER_EXCLUSIVE,
          priority: COUPON_PRIORITY.NEW_USER_EXCLUSIVE,
          discount: newUserExclusive[0].content,
          commodityCodes: (
            newUserExclusive[0].applicableGoods as unknown as string
          ).split(','),
          leftSeconds: newUserExclusive[0].leftSeconds || 0,
          rawData: newUserExclusive,
        });
      }

      const normalCoupons = couponList.filter(
        (item) =>
          item.status === 1 &&
          item.isValidityPeriod &&
          item.mode !== 1 &&
          item.mode !== 2,
      );
      if (normalCoupons.length) {
        coupons.push({
          type: CouponType.NORMAL_COUPON,
          priority: COUPON_PRIORITY.NORMAL_COUPON,
          discount: normalCoupons[0].content,
          commodityCodes: (normalCoupons[0].applicableGoods as unknown as string).split(
            ',',
          ),
          rawData: normalCoupons,
        });
      }

      return coupons;
    } catch {
      return [];
    }
  }

  private isValid(coupon: StandardCoupon): boolean {
    if (coupon.type === CouponType.NEW_USER_EXCLUSIVE) {
      return (coupon.leftSeconds || 0) > 0;
    }
    return true;
  }
}
